import { useCallback } from 'react';
import c from 'classnames';

import useToggle from '@lyearn/core/hooks/useToggle';
import { ArrowRightFillLegacy } from '@lyearn/icons';
import { useNodeStatesContext, useReactNodeView } from '@lyearn/lygo-core';
import { Box, Stack } from '@lyearn/ui';

import { insertNode, selectionType } from '../../../utils';

const ToggleListItem: React.FC = () => {
  const { editableComponentRef, node, view, getPos } = useReactNodeView();
  const [expanded, { toggle }] = useToggle(false);
  const { setNodeState } = useNodeStatesContext();

  const nodeId = node?.attrs.id;

  const handleToggle = useCallback(() => {
    if (!nodeId) {
      return;
    }
    setNodeState?.(nodeId, { expanded: !expanded });
    toggle();
  }, [expanded, nodeId, setNodeState, toggle]);

  const addNewParagraphNodeAtEnd = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      const nodePos = typeof getPos === 'function' && getPos();
      const toggleListContentNode = node?.lastChild;
      if (!node || !view || !nodePos || !toggleListContentNode?.lastChild) {
        return;
      }

      if (
        ![view.state.schema.nodes?.paragraph, view.state.schema.nodes?.heading].includes(
          toggleListContentNode.lastChild.type,
        )
      ) {
        insertNode(
          view,
          selectionType.NODE,
          view.state.schema.nodes.paragraph.create(),
          nodePos + node.nodeSize - 2,
        );
      }
    },
    [getPos, node, view],
  );

  return (
    <>
      <Stack className="mt-16 w-full">
        <Box
          className="mt-4 h-24 cursor-pointer me-12"
          contentEditable={false}
          role="button"
          onClick={handleToggle}>
          <ArrowRightFillLegacy
            className={c(
              {
                'rotate-90 transform': expanded,
              },
              'transition-all duration-300 ease-out rtl:rotate-180',
              'rounded-4 hover:bg-interactive-secondary',
            )}
            height={20}
            width={20}
          />
        </Box>
        <div ref={editableComponentRef} className="w-full" />
      </Stack>
      <Box className={c({ hidden: !expanded })} contentEditable={false}>
        <button
          className="h-20 w-full cursor-default opacity-0"
          onClick={addNewParagraphNodeAtEnd}
        />
      </Box>
    </>
  );
};

export default ToggleListItem;
